import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Minus, Plus, RotateCcw, Search, X, Bell, RefreshCw, Loader2, ArrowRight, Trash2, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mapApiProviderToComponent } from '@/features/games/data/gameProviders';
import UserRegistration from '@/features/auth/components/UserRegistration';
import SelectAmountToDeposit from '@/features/finance/components/SelectAmountToDeposit';
import { type UserCredentials, authService } from '@/features/auth/services/authService';
import { apiService } from '@/services/apiService';
import { depositService, type DepositData } from '@/features/finance/services/depositService';
import { clearUserDataWithReason } from '@/utils/userDataUtils';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { useHomeGridCapacity } from '@/hooks/useHomeGridCapacity';
import { notificationService } from '@/services/notificationService';
import NotificationContainer from '@/components/NotificationContainer';
import { playClickSound } from '@/utils/soundUtils';
import { getGameProviders, getCachedProvidersSync, preloadProviderIcons } from '@/features/games/services/gameProviderService';
import GameProviderCard from '@/features/games/components/GameProviderCard';
import BottomWinTicker from '@/features/games/components/BottomWinTicker';
import APP_CONFIG from '@/config/appConfig';
import OrderSummaryFooter from '@/layouts/components/Footer/OrderSummaryFooter';
import OrderSummaryModal from '@/features/finance/components/OrderSummaryModal';
import ManualDepositWarningModal from '@/features/finance/components/ManualDepositWarningModal';

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines
// To enable demo mode: Uncomment the next 2 lines
import DemoWrapper from '@/components/demo/DemoWrapper';

interface GameProvider {
    id: string;
    code?: string;
    name: string;
    tagline: string;
    gradient: string;
    icon: string; // Path to icon image
    status?: 'active' | 'inactive' | 'maintenance';
    /** Deposit type: 'auto' = instant, 'manual' = staff processes (5min-2hrs) */
    depositType?: 'auto' | 'manual';
}

// Local GameProviderCard component removed - using centralized component from @/features/games/components/GameProviderCard

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines and uncomment the last line
// To enable demo mode: Uncomment the next 2 lines and comment out the last line
function KioskGameProvidersContent() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedProvider, setSelectedProvider] = useState<GameProvider | null>(null);
    const [currentPage, setCurrentPage] = useState<'providers' | 'actions' | 'registration' | 'deposit-amount'>('providers');
    const [selectedAction, setSelectedAction] = useState<'deposit' | 'withdrawal' | null>(null);
    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
    const [authenticatedUser, setAuthenticatedUser] = useState<{
        email: string;
        user?: any;
        token?: string;
    } | null>(null);
    const [providers, setProviders] = useState<GameProvider[]>([]);
    const [isLoadingProviders, setIsLoadingProviders] = useState<boolean>(true);
    const [providersError, setProvidersError] = useState<string | null>(null);
    // Map to store provider status (id -> status)
    const [providerStatusMap, setProviderStatusMap] = useState<Map<string, 'active' | 'inactive' | 'maintenance'>>(new Map());
    // Map to store amounts for each provider (id -> amount)
    const [providerAmounts, setProviderAmounts] = useState<Map<string, number>>(new Map());
    // Pagination state
    const [paginationPage, setPaginationPage] = useState(1); // Start at page 1 (user-friendly)
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    const gridAreaRef = useRef<HTMLDivElement>(null);
    // Dynamic cards per page from available height (no scroll)
    const cardsPerPage = useHomeGridCapacity(cardsContainerRef);
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [pageJumpInput, setPageJumpInput] = useState('');
    // Swipe gesture state
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    // Mouse drag state for desktop swipe
    const [mouseStart, setMouseStart] = useState<number | null>(null);
    const [mouseEnd, setMouseEnd] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    // Order summary popup state
    const [showOrderSummary, setShowOrderSummary] = useState(false);
    // Track if user is going to wallet (vs deposit flow)
    const [isWalletFlow, setIsWalletFlow] = useState(false);
    // Manual deposit warning state
    const [showManualDepositWarning, setShowManualDepositWarning] = useState(false);
    // Wallet balance state
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);
    const [viewportHeight, setViewportHeight] = useState<number>(() =>
        typeof window !== 'undefined' ? window.innerHeight : 900
    );

    const preloadGameIcons = useCallback((providerItems: Array<{ code?: string }> = []) => {
        void preloadProviderIcons(providerItems.map((item) => item.code)).catch(() => {
            // Silent by design: icon preload must not affect UX flow.
        });
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const updateViewportHeight = () => setViewportHeight(window.innerHeight);
        updateViewportHeight();
        window.addEventListener('resize', updateViewportHeight);
        window.addEventListener('orientationchange', updateViewportHeight);
        return () => {
            window.removeEventListener('resize', updateViewportHeight);
            window.removeEventListener('orientationchange', updateViewportHeight);
        };
    }, []);

    const isUltraCompactHeight = viewportHeight <= 760;
    const isCompactHeight = viewportHeight <= 820;
    const isDesktopViewport = typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
    // Fallback only for extremely short viewports; normal devices keep fixed no-scroll layout.
    const allowPageScrollFallback = viewportHeight <= 680;

    // System-level scroll-to-top on page changes
    useScrollToTop(currentPage, { behavior: 'smooth', delay: 100 });

    // System-level full-screen background (uses centralized theme)
    const fullScreenBg = usePageFullScreenBackground();

    // Load wallet balance
    useEffect(() => {
        const loadBalance = async () => {
            try {
                setWalletBalanceLoading(true);
                const response = await apiService.get<{
                    balance: number;
                    currency: string;
                    lastUpdated: string;
                }>('/payment/balance', true);

                if (response.success && response.data) {
                    const data = response.data as any;
                    const balance = data.balance ?? data.data?.balance ?? 0;
                    setWalletBalance(balance);
                }
            } catch (error: unknown) {
                console.error('[Home] Error loading wallet balance:', error);
            } finally {
                setWalletBalanceLoading(false);
            }
        };

        loadBalance();
    }, []);

    const handleReloadBalance = async () => {
        playClickSound();
        try {
            setWalletBalanceLoading(true);
            const response = await apiService.get<{
                balance: number;
                currency: string;
                lastUpdated: string;
            }>('/payment/balance', true, true); // forceRefresh = true

            if (response.success && response.data) {
                const data = response.data as any;
                const balance = data.balance ?? data.data?.balance ?? 0;
                setWalletBalance(balance);
            }
        } catch (error: unknown) {
            console.error('[Home] Error reloading wallet balance:', error);
        } finally {
            setWalletBalanceLoading(false);
        }
    };

    // Check if we should show login form (e.g., from "Back to Login" from forgot password)
    useEffect(() => {
        const showLogin = searchParams.get('showLogin');
        if (showLogin === 'true') {
            navigate('/login');
            // Remove the query parameter from URL
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, navigate]);

    // On kiosk hosts, if a session token exists, send the user to account (same entry behavior without "Remember me" UI)
    useEffect(() => {
        const KIOSK_AUTO_ACCOUNT_DOMAINS = ['m.kioskservice.club', 'client.kioskservice.club', 'localhost'];
        const currentHost = window.location.hostname;
        const accessToken = localStorage.getItem('accessToken');

        if (KIOSK_AUTO_ACCOUNT_DOMAINS.includes(currentHost) && accessToken) {
            navigate('/account');
            return;
        }

        if (apiService.isAuthenticated()) {
            apiService.clearTokens();
            apiService.clearUserCache();
            console.log('🧹 Cleared authentication tokens - user must login again');
        }
    }, [navigate]);

    // Fetch game providers from API
    useEffect(() => {
        const fetchProviders = async () => {
            // Check API cache first for instant display
            const cached = getCachedProvidersSync();

            if (cached && cached.success && cached.data?.items && cached.data.items.length > 0) {
                const statusMap = new Map<string, 'active' | 'inactive' | 'maintenance'>();
                cached.data.items.forEach(p => statusMap.set(p.id, p.status));
                setProviderStatusMap(statusMap);

                const mappedProviders = cached.data.items
                    .map(apiProvider => mapApiProviderToComponent(apiProvider))
                    .filter((provider): provider is GameProvider => provider !== null)
                    .map(provider => ({ ...provider, status: statusMap.get(provider.id) || 'inactive' }));

                if (mappedProviders.length > 0) {
                    setProviders(mappedProviders);
                    setIsLoadingProviders(false);
                    preloadGameIcons(cached.data.items);
                    // Refresh in background (respect service cache TTL to avoid duplicate bursts)
                    getGameProviders(false).then(freshResponse => {
                        if (freshResponse.success && freshResponse.data?.items && freshResponse.data.items.length > 0) {
                            const freshStatusMap = new Map<string, 'active' | 'inactive' | 'maintenance'>();
                            freshResponse.data.items.forEach(p => freshStatusMap.set(p.id, p.status));
                            setProviderStatusMap(freshStatusMap);

                            const freshProviders = freshResponse.data.items
                                .map(apiProvider => mapApiProviderToComponent(apiProvider))
                                .filter((provider): provider is GameProvider => provider !== null)
                                .map(provider => ({ ...provider, status: freshStatusMap.get(provider.id) || 'inactive' }));
                            if (freshProviders.length > 0) {
                                setProviders(freshProviders);
                                preloadGameIcons(freshResponse.data.items);
                            }
                        }
                    }).catch(() => { /* silent */ });
                    return;
                }
            }

            // No cache - fetch from API with loading state
            setIsLoadingProviders(true);
            setProvidersError(null);

            try {
                const response = await getGameProviders(false);

                if (response.success && response.data?.items && response.data.items.length > 0) {
                    const statusMap = new Map<string, 'active' | 'inactive' | 'maintenance'>();
                    response.data.items.forEach(p => statusMap.set(p.id, p.status));
                    setProviderStatusMap(statusMap);

                    const mappedProviders = response.data.items
                        .map(apiProvider => mapApiProviderToComponent(apiProvider))
                        .filter((provider): provider is GameProvider => provider !== null)
                        .map(provider => ({ ...provider, status: statusMap.get(provider.id) || 'inactive' }));

                    setProviders(mappedProviders);
                    preloadGameIcons(response.data.items);
                } else {
                    setProvidersError('No game providers available. Please try again later.');
                }
            } catch (error: any) {
                console.error('[Home] Error fetching game providers:', error);
                setProvidersError('Failed to load game providers. Please try again later.');
            } finally {
                setIsLoadingProviders(false);
            }
        };

        fetchProviders();
    }, [preloadGameIcons]);

    // Fixed cards per page: 16 cards per page
    // Removed dynamic calculation - using fixed value as requested

    // Reset to first page when providers change
    useEffect(() => {
        setPaginationPage(1);
    }, [providers.length]);


    // Handle amount changes for providers – memoized to prevent child re-renders
    const handleAmountChange = useCallback((providerId: string, amount: number) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            newMap.set(providerId, amount);
            return newMap;
        });
    }, []);

    const handleIncrement = useCallback((providerId: string) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            const currentAmount = newMap.get(providerId) || 0;
            const jumpAmount = APP_CONFIG.jumpingEachClick;
            const newAmount = Math.min(currentAmount + jumpAmount, 10000);
            newMap.set(providerId, newAmount);
            return newMap;
        });
    }, []);

    const handleDecrement = useCallback((providerId: string) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            const currentAmount = newMap.get(providerId) || 0;
            if (currentAmount > 0) {
                const jumpAmount = APP_CONFIG.jumpingEachClick;
                const newAmount = Math.max(currentAmount - jumpAmount, 0);
                newMap.set(providerId, newAmount);
            } else {
                newMap.set(providerId, 0);
            }
            return newMap;
        });
    }, []);

    const handleResetAll = useCallback(() => {
        playClickSound();
        setProviderAmounts(new Map());
        setShowOrderSummary(false);
    }, []);

    const handleRemoveItem = useCallback((providerId: string) => {
        playClickSound();
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            newMap.delete(providerId);
            return newMap;
        });
    }, []);

    /**
     * Gets list of manual game providers that have amounts > 0.
     */
    const getSelectedManualProviders = () => {
        return providers.filter(p => {
            const amount = providerAmounts.get(p.id) || 0;
            return amount > 0 && p.depositType === 'manual';
        });
    };

    /**
     * Gets total amount for selected manual providers.
     */
    const getManualProvidersTotalAmount = () => {
        return getSelectedManualProviders().reduce((sum, p) => sum + (providerAmounts.get(p.id) || 0), 0);
    };

    /**
     * Saves pending game deposit amounts and continues to deposit page (wallet-only flow).
     */
    const navigateToDepositFlow = () => {
        const amountsArray = Array.from(providerAmounts.entries())
            .filter(([_, amount]) => amount > 0)
            .map(([providerId, amount]) => ({ providerId, amount }));

        if (amountsArray.length > 0) {
            const pendingData = {
                amounts: amountsArray,
                timestamp: Date.now(),
                source: 'continue_button',
            };

            try {
                sessionStorage.setItem('pending_deposit_amounts', JSON.stringify(pendingData));
                console.log('💾 Saved pending deposit amounts to sessionStorage:', pendingData);
            } catch (error) {
                console.error('Failed to save to sessionStorage:', error);
            }
        }

        setShowOrderSummary(false);
        setShowManualDepositWarning(false);
        setIsWalletFlow(false);

        const token = apiService.getAccessToken();
        if (token) {
            navigate('/account/finance/deposit');
            return;
        }

        sessionStorage.setItem('login_redirect_to', '/account/finance/deposit');
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('providerId', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        if (selectedAction) {
            params.set('action', selectedAction);
        }
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    // Handle proceed to funding
    const handleProceedToFunding = () => {
        playClickSound();

        const manualProviders = getSelectedManualProviders();
        if (manualProviders.length > 0) {
            setShowManualDepositWarning(true);
            return;
        }

        navigateToDepositFlow();
    };

    // Memoized calculations – avoid recalculating on every render
    const totalAmount = useMemo(
        () => Array.from(providerAmounts.values()).reduce((sum, amount) => sum + amount, 0),
        [providerAmounts]
    );

    const filteredCards = useMemo(
        () => searchQuery.trim()
            ? providers.filter(card =>
                card.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (card.tagline && card.tagline.toLowerCase().includes(searchQuery.toLowerCase()))
            )
            : providers,
        [providers, searchQuery]
    );

    // Pagination logic – memoized
    const { totalPages, currentPageCards } = useMemo(() => {
        const totalCards = filteredCards.length;
        const tp = Math.max(1, Math.ceil(totalCards / cardsPerPage));
        const startIndex = (paginationPage - 1) * cardsPerPage;
        const endIndex = startIndex + cardsPerPage;
        return { totalPages: tp, currentPageCards: filteredCards.slice(startIndex, endIndex) };
    }, [filteredCards, cardsPerPage, paginationPage]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setPaginationPage(1);
    }, [searchQuery]);

    // Pagination handlers
    const handlePreviousPage = () => {
        if (paginationPage > 1) {
            playClickSound();
            setPaginationPage(paginationPage - 1);
            // Scroll to top of cards container
            if (cardsContainerRef.current) {
                cardsContainerRef.current.scrollTop = 0;
            }
        }
    };

    const handleNextPage = () => {
        if (paginationPage < totalPages) {
            playClickSound();
            setPaginationPage(paginationPage + 1);
            // Scroll to top of cards container
            if (cardsContainerRef.current) {
                cardsContainerRef.current.scrollTop = 0;
            }
        }
    };

    const handleGoToPage = (page: number) => {
        if (page >= 1 && page <= totalPages && page !== paginationPage) {
            playClickSound();
            setPaginationPage(page);
            setPageJumpInput(''); // Clear input after jump
            // Scroll to top of cards container
            if (cardsContainerRef.current) {
                cardsContainerRef.current.scrollTop = 0;
            }
        }
    };

    const handlePageJump = () => {
        const pageNum = parseInt(pageJumpInput);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
            handleGoToPage(pageNum);
        } else {
            setPageJumpInput('');
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setPaginationPage(1); // Reset to first page when searching
    };

    // Swipe gesture handlers
    const minSwipeDistance = 50; // Minimum distance for a swipe (in pixels)

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && paginationPage < totalPages) {
            // Swipe left = next page
            handleNextPage();
        }
        if (isRightSwipe && paginationPage > 1) {
            // Swipe right = previous page
            handlePreviousPage();
        }
    };

    // Mouse drag handlers for desktop (optional)
    const onMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setMouseEnd(null);
        setMouseStart(e.clientX);
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        setMouseEnd(e.clientX);
    };

    const onMouseUp = () => {
        if (!isDragging) return;

        if (mouseStart !== null && mouseEnd !== null) {
            const distance = mouseStart - mouseEnd;
            const isLeftSwipe = distance > minSwipeDistance;
            const isRightSwipe = distance < -minSwipeDistance;

            if (isLeftSwipe && paginationPage < totalPages) {
                handleNextPage();
            }
            if (isRightSwipe && paginationPage > 1) {
                handlePreviousPage();
            }
        }

        setIsDragging(false);
        setMouseStart(null);
        setMouseEnd(null);
    };

    const onMouseLeave = () => {
        setIsDragging(false);
        setMouseStart(null);
        setMouseEnd(null);
    };

    const handleProviderSelect = (provider: GameProvider) => {
        playClickSound();
        // Clear any previous user data when selecting a new provider
        clearUserDataWithReason('New provider selected - clearing previous user data');
        setAuthenticatedUser(null);
        setSelectedAction(null);
        setSelectedAmount(null);
        setSelectedProvider(provider);

        // Navigate directly to login page with deposit action (skip action selection screen)
        const params = new URLSearchParams();
        params.set('providerId', String(provider.id));
        params.set('providerName', provider.name);
        params.set('action', 'deposit');
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    const handleBackToProviders = () => {
        playClickSound();
        // Clear user data when going back to platform selection
        clearUserDataWithReason('Navigating back to platform selection');
        setAuthenticatedUser(null);
        setSelectedProvider(null);
        setSelectedAction(null);
        setSelectedAmount(null);
        setCurrentPage('providers');
    };

    const handleActionSelect = (action: 'deposit' | 'withdrawal') => {
        playClickSound();
        setSelectedAction(action);

        // Navigate to login page with provider and action info
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('providerId', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        params.set('action', action);
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    const handleBackToActions = () => {
        playClickSound();
        // Clear deposit amount data and user authentication when going back to actions
        clearUserDataWithReason('Returning to action selection from deposit amount');
        setSelectedAmount(null);
        setAuthenticatedUser(null); // Clear user email/login info
        setIsWalletFlow(false); // Reset wallet flow when going back
        setCurrentPage('actions');
    };

    const computeProviderSlug = (name: string) =>
        name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

    const handleAuthenticate = async (credentials: UserCredentials) => {
        console.log('Authentication successful:', {
            provider: selectedProvider?.name,
            action: selectedAction,
            credentials: {
                email: credentials.email,
                // Don't log password for security
                hasPassword: !!credentials.password,
                emailCode: credentials.emailCode
            }
        });

        // Authentication was already completed in UserAuthentication component
        // Just store the user data and navigate to next step
        try {
            // Get the current user data from the auth service
            const currentUser = await authService.getCurrentUser();

            if (currentUser.success) {
                // Store user data
                setAuthenticatedUser({
                    email: credentials.email,
                    user: currentUser.data?.user,
                    token: apiService.getAccessToken() || undefined
                });

                // Redirect based on flow type
                if (isWalletFlow) {
                    // If wallet flow, redirect to account page
                    notificationService.success(`Authentication successful for ${credentials.email}`);
                    navigate('/account');
                } else {
                    notificationService.success(`Authentication successful for ${credentials.email}`);
                    navigate('/account/finance/deposit');
                }
            } else {
                notificationService.error('Failed to get user data after authentication');
            }
        } catch (error: any) {
            console.error('Error getting user data:', error);
            notificationService.error('Authentication completed but failed to get user data');
        }
    };

    const handleRegister = () => {
        playClickSound();
        // Clear any existing user data when starting registration
        clearUserDataWithReason('Starting new user registration');
        setAuthenticatedUser(null);
        // Navigate to registration page (with or without provider)
        setCurrentPage('registration');
    };

    const handleBackToAuthentication = () => {
        playClickSound();
        // Clear any registration data when going back to authentication
        clearUserDataWithReason('Returning to authentication from registration');
        setAuthenticatedUser(null);

        // Navigate to login page with provider and action info if available
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('providerId', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        if (selectedAction) {
            params.set('action', selectedAction);
        }
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    const handleRegistrationSuccess = (email: string) => {
        console.log('Registration successful for:', {
            provider: selectedProvider?.name,
            action: selectedAction,
            email: email
        });

        // Clear any existing user data and navigate to login page
        clearUserDataWithReason('Registration complete - redirecting to login');
        setAuthenticatedUser(null);

        // Navigate to login page with provider and action info if available
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('providerId', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        if (selectedAction) {
            params.set('action', selectedAction);
        }
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    const handleAmountSelected = async (amount: number) => {
        if (!selectedProvider || !authenticatedUser) return;

        setSelectedAmount(amount);

        // Process deposit with authenticated user
        // TODO: Replace with actual API call in production
        try {
            const depositData: DepositData = {
                amount,
                providerId: selectedProvider.id,
                providerName: selectedProvider.name,
                userId: authenticatedUser.email // Include authenticated user
            };

            console.log('Processing deposit for authenticated user:', {
                user: authenticatedUser.email,
                depositData
            });

            const result = await depositService.processDepositBypass(depositData);

            if (result.success) {
                console.log('✅ Deposit successful:', result);
                // TODO: Navigate to success page or show success message
                alert(`Deposit successful for ${authenticatedUser.email}! Transaction ID: ${result.data?.transactionId}`);
            } else {
                console.error('❌ Deposit failed:', result);
                alert(`Deposit failed: ${result.message}`);
            }
        } catch (error) {
            console.error('Error processing deposit:', error);
            alert('An error occurred while processing your deposit. Please try again.');
        }
    };

    // Action selection screen removed - providers now navigate directly to login


    if (currentPage === 'registration') {
        // Allow registration with or without provider
        if (selectedProvider) {
            return (
                <UserRegistration
                    selectedProvider={selectedProvider}
                    onBack={handleBackToAuthentication}
                    onRegistrationSuccess={handleRegistrationSuccess}
                />
            );
        } else {
            // Registration without provider (from direct login)
            // Use a default provider or allow registration without provider
            return (
                <UserRegistration
                    selectedProvider={{
                        id: '0',
                        name: 'Kiosk Gaming',
                        tagline: 'Join our gaming platform',
                        gradient: 'from-purple-500 to-pink-500',
                        icon: '/icons/egame.jpg'
                    }}
                    onBack={() => {
                        playClickSound();
                        // Navigate to login page
                        navigate('/login');
                    }}
                    onRegistrationSuccess={handleRegistrationSuccess}
                />
            );
        }
    }

    if (currentPage === 'deposit-amount' && selectedProvider && selectedAction === 'deposit' && authenticatedUser) {
        return (
            <SelectAmountToDeposit
                selectedProvider={selectedProvider}
                authenticatedUser={authenticatedUser}
                onBack={handleBackToActions}
                onAmountSelected={handleAmountSelected}
            />
        );
    }

    return (<div {...fullScreenBg} className={`${fullScreenBg.className} ${isDesktopViewport ? 'overflow-x-hidden h-full max-h-full' : 'overflow-hidden h-[100dvh] max-h-[100dvh]'} flex flex-col relative min-w-0`}>
        {/* Body - fills layout main (header 80px, footer 80px); no scroll */}
        <main className={`flex-1 min-h-0 min-w-0 flex flex-col ${isDesktopViewport || allowPageScrollFallback ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`}>
            <div
                className={`mx-auto w-full min-w-0 flex-1 min-h-0 flex flex-col px-3 sm:px-4 md:px-6 ipad:px-6 lg:px-8 ${isUltraCompactHeight ? 'pt-2 pb-1' : isCompactHeight ? 'pt-3 pb-1.5' : 'pt-4 pb-2'} sm:pt-6 ipad:pt-6 ipad:pb-3 sm:pb-3`}
                style={{ maxWidth: '1280px' }}
            >
                {/* Gaming Platform Title Section - Mobile only (hidden on desktop md+) */}
                <div className={`flex-shrink-0 flex flex-col items-center ${isUltraCompactHeight ? 'gap-1 mb-2.5' : isCompactHeight ? 'gap-1.5 mb-3' : 'gap-1.5 mb-4'} sm:gap-2 ipad:gap-2 sm:mb-6 ipad:mb-6 lg:hidden`}>
                    <h1 className={`${isUltraCompactHeight ? 'text-[24px]' : isCompactHeight ? 'text-[27px]' : 'text-[29px]'} sm:text-3xl text-white font-black italic tracking-wide text-center`}>
                        {t('header.gamingPlatform')}
                    </h1>
                    <div className="h-0.5 sm:h-1 w-16 sm:w-20 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full" />
                    <p className={`${isUltraCompactHeight ? 'text-[12px]' : 'text-[14px]'} sm:text-sm text-white/60 font-semibold tracking-wider text-center`}>
                        {t('header.selectPlatformsToDeposit')}
                    </p>
                </div>

                {/* Search - compact bar */}
                <div className={`flex-shrink-0 flex justify-center ${isUltraCompactHeight ? 'mb-1' : 'mb-1.5'} sm:mb-2`}>
                    <div
                        className={`relative flex items-center w-full max-w-[320px] sm:max-w-[360px] px-2 ${isUltraCompactHeight ? 'py-0.5' : 'py-1'} sm:px-2 sm:py-1 rounded-md border border-white/10`}
                        style={{
                            background: 'rgba(255, 255, 255, 0.07)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        }}
                    >
                        <Search className="flex-shrink-0 w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/50 mr-1.5" />
                        <input
                            type="text"
                            placeholder={t('games.searchGamesPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => {
                                const value = e.target.value.slice(0, 10);
                                handleSearchChange(value);
                            }}
                            maxLength={10}
                            className="flex-1 min-w-0 bg-transparent border-none pl-0 pr-5 py-0.5 text-[12px] sm:text-[13px] text-white placeholder-white/50 focus:outline-none focus:ring-0"
                            style={{
                                fontFamily: 'Arial, Helvetica, sans-serif',
                                fontWeight: 400,
                                color: '#fffefe80',
                            }}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => handleSearchChange('')}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-0.5"
                                aria-label="Clear search"
                            >
                                <X className="w-2.5 h-2.5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Cards Container - grid area height drives cardsPerPage (no scroll) */}
                <div ref={gridAreaRef} className="relative flex-1 min-h-0 flex flex-col">
                    {/* Cards Grid Container with Swipe Gestures */}
                    <div
                        ref={cardsContainerRef}
                        className={`flex-1 min-h-0 px-2 sm:px-3 md:px-4 ipad:px-4 ${isUltraCompactHeight ? 'py-1.5' : isCompactHeight ? 'py-2' : 'py-3'} sm:py-4 ipad:py-4 select-none overflow-hidden`}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseLeave}
                        style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
                    >
                        {/* Loading State */}
                        {isLoadingProviders && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-2.5 md:gap-3 lg:gap-4 max-w-[420px] sm:max-w-[560px] md:max-w-[720px] lg:max-w-[880px] mx-auto">
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                    <div
                                        key={i}
                                        className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 border border-white/20 animate-pulse h-[92px] sm:h-[100px] lg:h-[96px]"
                                    >
                                        <div className="flex flex-col items-center justify-center h-full space-y-2">
                                            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-10 lg:h-10 rounded-full bg-white/10" />
                                            <div className="w-16 sm:w-20 lg:w-14 h-3 sm:h-4 bg-white/10 rounded" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Error State */}
                        {!isLoadingProviders && providersError && (
                            <div className="mb-4 text-center">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 backdrop-blur-md rounded-full border border-red-500/30">
                                    <span className="text-red-200 text-sm font-medium">{providersError}</span>
                                </div>
                            </div>
                        )}

                        {/* Provider Grid - Paginated */}
                        {/* Mobile: 2 columns, Tablet/Desktop lg (768px-1279px): 3 columns, Desktop xl (1280px+): 4 columns */}
                        {!isLoadingProviders && providers.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-2.5 md:gap-3 lg:gap-4 max-w-[420px] sm:max-w-[560px] md:max-w-[720px] lg:max-w-[880px] mx-auto">
                                {currentPageCards.map((card: GameProvider, index: number) => {
                                    // Regular provider card
                                    const provider = card as GameProvider;
                                    const status = providerStatusMap.get(provider.id) || provider.status || 'inactive';
                                    const amount = providerAmounts.get(provider.id) || 0;
                                    const originalIndex = providers.findIndex(p => p.id === provider.id);

                                    const statusText = provider.status === 'inactive' ? 'COMING SOON' : 'ACTIVE SESSION';

                                    return (
                                        <GameProviderCard
                                            key={provider.id}
                                            provider={provider}
                                            providerStatus={status}
                                            amount={amount}
                                            onAmountChange={handleAmountChange}
                                            onIncrement={handleIncrement}
                                            onDecrement={handleDecrement}
                                            statusText={statusText}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        {/* Empty State - No Providers */}
                        {!isLoadingProviders && providers.length === 0 && !providersError && (
                            <div className="text-center">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 backdrop-blur-md rounded-full border border-yellow-500/30">
                                    <span className="text-yellow-200 text-sm font-medium">No game providers available at the moment</span>
                                </div>
                            </div>
                        )}

                        {/* Empty State - No Search Results */}
                        {!isLoadingProviders && providers.length > 0 && searchQuery.trim() && filteredCards.length === 0 && (
                            <div className="text-center py-8">
                                <div className="inline-flex flex-col items-center gap-3 px-4 py-3 bg-yellow-500/20 backdrop-blur-md rounded-lg border border-yellow-500/30">
                                    <span className="text-yellow-200 text-sm font-medium">No games found matching "{searchQuery}"</span>
                                    <button
                                        onClick={() => handleSearchChange('')}
                                        className="px-4 py-2 bg-yellow-500/40 hover:bg-yellow-500/60 text-yellow-100 font-medium text-sm rounded-lg border border-yellow-400/50 transition-all duration-200 active:scale-95 shadow-md"
                                    >
                                        Clear search
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Compact Pagination - Only show if more than 1 page */}
                    {totalPages > 1 && (
                        <div className={`flex justify-center ${isUltraCompactHeight ? 'mt-1.5 mb-0.5' : 'mt-2.5 mb-1.5'} px-2`}>
                            <div className={`relative ${isUltraCompactHeight ? 'h-8' : 'h-9'} sm:h-10 w-full max-w-[260px] sm:max-w-[300px] flex items-center gap-2`}>
                                {/* Prev Button */}
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={paginationPage === 1}
                                    className={`
                                        h-full flex-1 min-w-0 flex items-center justify-center gap-1
                                        bg-white/10 border-none rounded-lg px-2 py-1
                                        transition-all duration-200
                                        ${paginationPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/15 active:scale-95'}
                                    `}
                                    style={{ backgroundColor: 'rgba(255, 254, 254, 0.1)' }}
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5 text-white flex-shrink-0" />
                                    <span className="text-white text-[12px] sm:text-sm font-semibold hidden sm:inline">Prev</span>
                                </button>

                                {/* Page Indicator */}
                                <div className="flex items-center justify-center gap-1 shrink-0">
                                    <span className="text-white/90 text-[12px] sm:text-sm font-bold">{paginationPage}</span>
                                    <span className="text-white/60 text-[12px] sm:text-sm">/</span>
                                    <span className="text-white/70 text-[12px] sm:text-sm font-medium">{totalPages}</span>
                                </div>

                                {/* Go to Page Input */}
                                <div className="w-12 sm:w-14 h-full flex items-center shrink-0">
                                    <input
                                        type="number"
                                        value={pageJumpInput}
                                        onChange={(e) => setPageJumpInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handlePageJump(); }}
                                        placeholder="Go"
                                        min={1}
                                        max={totalPages}
                                        className="w-full h-8 sm:h-9 bg-white/10 border border-white/20 rounded-md text-[12px] sm:text-sm text-white/80 text-center placeholder-white/50 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        style={{ color: 'rgba(255, 254, 254, 0.5)' }}
                                    />
                                </div>

                                {/* Next Button */}
                                <button
                                    onClick={handleNextPage}
                                    disabled={paginationPage === totalPages}
                                    className={`
                                        h-full flex-1 min-w-0 flex items-center justify-center gap-1
                                        bg-white/10 border-none rounded-lg px-2 py-1
                                        transition-all duration-200
                                        ${paginationPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/15 active:scale-95'}
                                    `}
                                    style={{ backgroundColor: 'rgba(255, 254, 254, 0.1)' }}
                                    aria-label="Next page"
                                >
                                    <span className="text-white text-[12px] sm:text-sm font-semibold hidden sm:inline">Next</span>
                                    <ChevronRight className="w-3.5 h-3.5 text-white flex-shrink-0" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>

        {/* Bottom ticker: full width, navy theme, one-line marquee */}
        <div className={`${isUltraCompactHeight ? 'h-[28px]' : isCompactHeight ? 'h-[30px]' : 'h-[34px]'} flex-shrink-0 overflow-hidden`}>
            <BottomWinTicker />
        </div>

        {/* Order Summary Footer - Based on Figma design */}
        {/* Footer - fixed 80px (layout: header 80, body flex, footer 80) */}
        <OrderSummaryFooter
            className="flex-shrink-0"
            totalAmount={totalAmount}
            onClearOrder={handleResetAll}
            onProceedToFunding={handleProceedToFunding}
            onShowOrderSummary={() => setShowOrderSummary(true)}
        />

        {/* Order Summary Modal */}
        <OrderSummaryModal
            isOpen={showOrderSummary}
            onClose={() => setShowOrderSummary(false)}
            totalAmount={totalAmount}
            providerAmounts={providerAmounts}
            providers={providers}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onRemoveItem={handleRemoveItem}
            selectedProvider={selectedProvider}
            selectedAction={selectedAction}
            onConfirm={() => {
                setIsWalletFlow(false);
            }}
        />

        {/* Manual Deposit Warning Modal */}
        <ManualDepositWarningModal
            isOpen={showManualDepositWarning}
            onClose={() => setShowManualDepositWarning(false)}
            onConfirm={navigateToDepositFlow}
            amount={getManualProvidersTotalAmount()}
            providerName={getSelectedManualProviders().map(p => p.name).join(', ')}
        />



        <style>{`
             @keyframes shimmer {
                 0% {
                     transform: translateX(-100%);
                 }
                 100% {
                     transform: translateX(100%);
                 }
             }

             .animate-shimmer {
                 animation: shimmer 2s infinite;
             }

             /* Hide scrollbar for Chrome, Safari and Opera */
             .scrollbar-hide::-webkit-scrollbar {
                 display: none;
             }

             /* Hide scrollbar for IE, Edge and Firefox */
             .scrollbar-hide {
                 -ms-overflow-style: none;  /* IE and Edge */
                 scrollbar-width: none;  /* Firefox */
             }

        `}</style>

        {/* Notification Container */}
        <NotificationContainer />
    </div>);
}

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines and uncomment the last line
// To enable demo mode: Uncomment the next 2 lines and comment out the last line
export default function KioskGameProviders() {
    return (
        <DemoWrapper>
            <KioskGameProvidersContent />
        </DemoWrapper>
    );
}

// PRODUCTION MODE - Use this when demo mode is disabled
// export default KioskGameProvidersContent;
